/**
 * Custom hook for common Nivo chart setup and configuration.
 * Consolidates shared logic from Line.jsx, LineIndex.jsx, Dashboard.jsx, etc.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { debounce } from '../utils/debounce';
import { getNivoTheme, getScatterTheme, getBarTheme } from '../constants/chartTheme';
import {
  getTickRotation,
  getTickCount,
  shouldShowLegend,
  calculateDynamicMin,
  calculateDynamicMax,
  calculateScatterRange,
  LEGEND_CONFIGS,
} from '../constants/chartConfig';
import {
  getStaticNivoProps,
  useChartPresentation,
} from '../components/charts/common/ChartPresentationContext';

/**
 * Hook for common Nivo chart setup
 * Handles window resize, theme, legend visibility, and axis tick configuration
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.theme - MUI theme object
 * @param {Array} options.data - Chart data array (for legend calculations)
 * @param {string} options.chartType - Chart type: 'line', 'bar', 'scatter' (for theme selection)
 * @param {string} options.legendPosition - Legend position preference: 'bottom', 'right', 'none'
 * @param {number} options.legendItemWidth - Width per legend item (default: 114)
 *
 * @returns {Object} Chart configuration values
 */
export const useNivoChart = ({
  theme,
  data = [],
  chartType = 'line',
  legendPosition = 'bottom',
  legendItemWidth = 114,
  presentation: presentationOverrides,
} = {}) => {
  const presentation = useChartPresentation(presentationOverrides);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  // Handle window resize with debounce
  useEffect(() => {
    const handleResize = debounce(() => {
      setWindowWidth(window.innerWidth);
    }, 150);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine dark mode
  const isDarkMode = theme?.palette?.mode === 'dark';

  // Get appropriate Nivo theme based on chart type
  const nivoTheme = useMemo(() => {
    if (!theme) return {};

    switch (chartType) {
      case 'scatter':
        return getScatterTheme(theme);
      case 'bar':
        return getBarTheme(theme);
      case 'line':
      default:
        return getNivoTheme(theme);
    }
  }, [theme, chartType]);

  // Calculate legend visibility
  const showLegend = useMemo(() => {
    if (legendPosition === 'none') return false;
    const seriesCount = Array.isArray(data) ? data.length : 0;
    return shouldShowLegend(presentation.width || windowWidth, seriesCount, legendItemWidth);
  }, [windowWidth, data, legendPosition, legendItemWidth, presentation.width]);

  // Get legend configuration
  const legendConfig = useMemo(() => {
    if (!showLegend) return [];

    switch (legendPosition) {
      case 'right':
        return [LEGEND_CONFIGS.TOP_RIGHT_COLUMN];
      case 'bottom':
      default:
        return [LEGEND_CONFIGS.BOTTOM_ROW];
    }
  }, [showLegend, legendPosition]);

  // Get tick rotation based on window width and chart type
  const getTickRotationValue = useCallback(
    (type = 'date') => {
      return getTickRotation(presentation.width || windowWidth, type);
    },
    [presentation.width, windowWidth]
  );

  // Get tick count based on window width
  const getTickCountValue = useCallback(
    (density = 'normal') => {
      return getTickCount(presentation.width || windowWidth, density);
    },
    [presentation.width, windowWidth]
  );

  // Calculate dynamic Y-axis minimum for line charts
  const dynamicYMin = useMemo(() => {
    if (chartType !== 'line' || !Array.isArray(data) || data.length === 0) {
      return 'auto';
    }
    return calculateDynamicMin(data);
  }, [data, chartType]);

  return {
    // Window state
    windowWidth,
    chartWidth: presentation.width || windowWidth,
    presentation,
    isStatic: Boolean(presentation.static),
    staticNivoProps: getStaticNivoProps(presentation),

    // Theme
    isDarkMode,
    nivoTheme,

    // Legend
    showLegend,
    legendConfig,

    // Axis helpers
    getTickRotation: getTickRotationValue,
    getTickCount: getTickCountValue,

    // Y-axis
    dynamicYMin,
  };
};

/**
 * Hook for scatter plot specific setup
 * Handles dynamic tick values based on data range
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.theme - MUI theme object
 * @param {Array} options.data - Chart data array
 * @param {number} options.xMax - Maximum X value
 * @param {number} options.yMax - Maximum Y value
 *
 * @returns {Object} Scatter plot configuration
 */
export const useScatterChart = ({ theme, data = [], xMax = 100, yMax = 100 } = {}) => {
  const baseConfig = useNivoChart({ theme, data, chartType: 'scatter' });
  const [xTickValues, setXTickValues] = useState([]);
  const [yTickValues, setYTickValues] = useState([]);

  // Calculate tick values based on window width and data range
  useEffect(() => {
    const calculateTickValues = () => {
      const width = baseConfig.windowWidth;

      // Number of ticks based on screen width
      const xTicks = Math.max(2, Math.floor(width / 100));
      const yTicks = Math.max(2, Math.floor(width / 100));

      // Calculate step size for y-axis (ensure whole numbers)
      const yStepSize = Math.ceil(yMax / (yTicks - 1));

      // Generate tick arrays
      const xTickArray = Array.from(
        { length: xTicks },
        (_, i) => (i / (xTicks - 1)) * xMax
      );

      const yTickArray = Array.from(
        { length: Math.ceil(yMax / yStepSize) + 1 },
        (_, i) => i * yStepSize
      );

      setXTickValues(xTickArray);
      setYTickValues(yTickArray);
    };

    calculateTickValues();
  }, [baseConfig.windowWidth, xMax, yMax]);

  return {
    ...baseConfig,
    xTickValues,
    yTickValues,
  };
};

/**
 * Hook for line chart with time-series data
 * Handles date formatting and rebasing
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.theme - MUI theme object
 * @param {Array} options.data - Chart data array
 * @param {boolean} options.rebase - Whether to rebase data to 100
 *
 * @returns {Object} Line chart configuration
 */
export const useLineChart = ({ theme, data = [], rebase = false } = {}) => {
  const baseConfig = useNivoChart({ theme, data, chartType: 'line' });

  // Rebase data if needed (for index charts)
  const rebasedData = useMemo(() => {
    if (!rebase || !Array.isArray(data) || data.length === 0) {
      return data;
    }

    return data.map((serie) => {
      const firstValue = serie.data[0]?.y || 1;
      return {
        ...serie, // Preserve all properties (isOverlay, roleKey, etc.)
        data: serie.data.map((point) => ({
          ...point, // Preserve any extra point properties
          x: point.x,
          y: (point.y / firstValue) * 100,
        })),
      };
    });
  }, [data, rebase]);

  // Calculate dynamic min for rebased data
  const dynamicYMin = useMemo(() => {
    const sourceData = rebase ? rebasedData : data;
    if (!Array.isArray(sourceData) || sourceData.length === 0) {
      return 'auto';
    }
    return calculateDynamicMin(sourceData);
  }, [rebasedData, data, rebase]);

  // Calculate dynamic max for rebased data (headroom so lines aren't clipped)
  const dynamicYMax = useMemo(() => {
    const sourceData = rebase ? rebasedData : data;
    if (!Array.isArray(sourceData) || sourceData.length === 0) {
      return 'auto';
    }
    return calculateDynamicMax(sourceData);
  }, [rebasedData, data, rebase]);

  return {
    ...baseConfig,
    rebasedData,
    dynamicYMin,
    dynamicYMax,
  };
};

/**
 * Hook for scatter chart with intelligent outlier handling
 * Calculates x/y ranges with outlier detection and transforms data for clamping
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.theme - MUI theme object
 * @param {Array} options.data - Chart data array in Nivo format [{ id, data: [{x, y, ...}] }]
 * @param {string} options.outlierMode - 'auto' | 'clamp' | 'show-all' (default: 'auto')
 * @param {number} options.iqrMultiplier - IQR multiplier for outlier detection (default: 3.0 for extreme only)
 * @param {number} options.xMaxCap - Hard maximum cap for x-axis (default: Infinity)
 * @param {number} options.yMaxCap - Hard maximum cap for y-axis (default: Infinity)
 *
 * @returns {Object} Scatter chart configuration with outlier handling
 */
export const useScatterChartWithOutliers = ({
  theme,
  data = [],
  outlierMode = 'auto',
  iqrMultiplier = 2.5, // Tighter detection - more outliers shown (was 3.0)
  xMaxCap = Infinity,
  yMaxCap = Infinity,
} = {}) => {
  const baseConfig = useNivoChart({ theme, data, chartType: 'scatter' });

  // Calculate ranges with outlier detection for both axes
  const xRange = useMemo(() => {
    return calculateScatterRange(data, 'x', {
      mode: outlierMode,
      iqrMultiplier,
      maxCap: xMaxCap,
    });
  }, [data, outlierMode, iqrMultiplier, xMaxCap]);

  const yRange = useMemo(() => {
    return calculateScatterRange(data, 'y', {
      mode: outlierMode,
      iqrMultiplier,
      maxCap: yMaxCap,
    });
  }, [data, outlierMode, iqrMultiplier, yMaxCap]);

  // Combine all clamped points from both axes
  const allClampedPoints = useMemo(() => {
    const clampedMap = new Map();

    // Add x-axis outliers
    xRange.clampedPoints.forEach((point) => {
      const key = `${point.ticker || point.index}-x`;
      clampedMap.set(key, { ...point, axis: 'x' });
    });

    // Add y-axis outliers
    yRange.clampedPoints.forEach((point) => {
      const key = `${point.ticker || point.index}-y`;
      const existing = clampedMap.get(`${point.ticker || point.index}-x`);
      if (existing) {
        // Point is an outlier on both axes - merge info
        existing.isYOutlier = true;
        existing.originalY = point.originalValue;
        existing.clampedY = point.clampedValue;
      } else {
        clampedMap.set(key, { ...point, axis: 'y' });
      }
    });

    return Array.from(clampedMap.values());
  }, [xRange.clampedPoints, yRange.clampedPoints]);

  // Transform data to clamp outlier positions
  const transformedData = useMemo(() => {
    if (outlierMode === 'show-all' || allClampedPoints.length === 0) {
      return data;
    }

    // Create lookup for clamped values
    const xClampedLookup = new Map();
    const yClampedLookup = new Map();

    xRange.clampedPoints.forEach((point) => {
      xClampedLookup.set(point.index, point);
    });

    yRange.clampedPoints.forEach((point) => {
      yClampedLookup.set(point.index, point);
    });

    // Track global point index
    let globalIndex = 0;

    return data.map((dataset) => ({
      ...dataset,
      data: (dataset.data || []).map((point) => {
        const currentIndex = globalIndex++;
        const xClamped = xClampedLookup.get(currentIndex);
        const yClamped = yClampedLookup.get(currentIndex);

        if (!xClamped && !yClamped) {
          return point;
        }

        return {
          ...point,
          // Store original values for tooltip
          originalX: point.x,
          originalY: point.y,
          // Apply clamped values - use 0.96 to leave room for icons
          x: xClamped ? Math.min(point.x, xRange.max * 0.96) : point.x,
          y: yClamped ? Math.min(point.y, yRange.max * 0.96) : point.y,
          isClamped: true,
          isXClamped: !!xClamped,
          isYClamped: !!yClamped,
        };
      }),
    }));
  }, [data, outlierMode, allClampedPoints, xRange, yRange]);

  // Calculate tick values based on window width and data range
  const xTickValues = useMemo(() => {
    return xRange.tickValues;
  }, [xRange.tickValues]);

  const yTickValues = useMemo(() => {
    return yRange.tickValues;
  }, [yRange.tickValues]);

  return {
    ...baseConfig,
    // Axis ranges
    xMin: xRange.min,
    xMax: xRange.max,
    yMin: yRange.min,
    yMax: yRange.max,
    // Tick values
    xTickValues,
    yTickValues,
    // Outlier information
    hasOutliers: xRange.hasOutliers || yRange.hasOutliers,
    clampedPoints: allClampedPoints,
    // Transformed data with clamped positions
    transformedData,
    // Raw range info for advanced usage
    xRange,
    yRange,
    // Axis range object for OutlierIndicatorLayer
    axisRanges: {
      x: { min: xRange.min, max: xRange.max },
      y: { min: yRange.min, max: yRange.max },
    },
  };
};

export default useNivoChart;
